import React, {useState} from 'react'
import { useParams, useLocation, useNavigate as useNativeNavigate } from 'react-router'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import {Container, Row, Col} from 'react-bootstrap'
import {GetFormat, getProvider, useGet} from 'sharedKernel/api'
import {Loading} from 'components/loading'
import {NoContent} from 'components/NoContent'
import {BaseButton} from 'iebt-zing-storybook'
import {trans} from 'sharedKernel/i18n'
import NoContentImg from 'assets/images/defaults/no_challenges.png'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {Filter} from 'components/Filter'
import Tippy from '@tippyjs/react'
import {followCursor} from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/scale.css'
import 'tippy.js/themes/translucent.css'
import {showToast} from 'components/toast'
import {triggerFileDownloadOnClientSide} from 'sharedKernel/file/handler'
import {PdfButtonStyled} from 'domain/feature/hub/evaluator/idea/evaluate/IdeaApplicationForm/PdfButton/styles'
import qs from 'qs'
import styled from 'styled-components'
import { MobileContainerPadding, ProgramTitle } from './index.style'

const StepInfoCard = styled.div`
  background-color: #f8f9fa;
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 767px) {
    padding: 0;
    background-color: transparent;
    border-radius: 0;
  }
`

const SolutionCard = styled.div`
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const SolutionsWrapper = styled.div`
  @media (max-width: 767px) {
    padding-left: 0;
    padding-right: 0;
  }
`

export const HubEvaluatorChallengeSolutionsPage = (): JSX.Element => {
    const {stepId, challengeId} = useParams()
    const navigate = useNavigate()
    const nativeNavigate = useNativeNavigate()
    const location = useLocation()
    const [currentSearchTerm, setCurrentSearchTerm] = useState<string>('')
    const {mutate: downloadFile} = getProvider()

    const labels = {
        noContent: trans('Nenhuma solução encontrada'),
        evaluate: trans('Avaliar'),
        viewEvaluation: trans('Ver avaliação'),
        exportSpreadsheet: trans('Exportar planilha'),
        noDataToExport: trans('Não há dados para exportar'),
    }

    const stepName = (location.state as any)?.stepName || 'ETAPA 1'
    const programName = (location.state as any)?.programName || labels.noContent
    const evaluationPeriod = (location.state as any)?.evaluationPeriod || ''



    type ProgressInfo = {
        bgColor: string
        color: string
        label: string
    }

    const availableProgressInfo = {
        finished: {
            bgColor: '#155724',
            color: '#d4edda',
            label: 'Finalizado'
        },
        started: {
            bgColor: '#fff3cd',
            color: '#856404',
            label: 'Iniciado'
        },
        pending: {
            bgColor: '#f8d7da',
            color: '#721c24',
            label: 'Não iniciado'
        }
    }

    const request: GetFormat = {
        url: `/v1/hub/evaluator/challenge/steps/${stepId}/solution`,
        httpConfig: {
            params: {
                status: ['approved', 'launched', 'created', 'canceled'],
                searchTherm: currentSearchTerm
            },
            paramsSerializer: params =>
                qs.stringify(params, {arrayFormat: 'repeat'})
        }
    }

    const {data, isLoading} = useGet(
        request,
        `evaluator-challenge-solutions-${stepId}-${currentSearchTerm}`
    )

    const hasSolutions = Boolean(data?.rows?.length)
    const isExportDisabled = isLoading || !hasSolutions

    const onSearch = (searchTerm: string): void => {
        setCurrentSearchTerm(searchTerm)
    }


    const renderContent = (): JSX.Element => {

        if (isLoading) {
            return <Loading/>
        }

        if (!data || !data.rows || data.rows.length === 0) {
            return <NoContent text={labels.noContent} image={NoContentImg} imageWidth='350' imageHeight='182'/>
        }

        const firstRow = data.rows[0]
        const challengeId = firstRow?.challengeId

        return (
            <>
                <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                    {data.rows.map((solution: any) => {
                        const started = solution.fillPercentage > 0;
                        let progressInfo: ProgressInfo
                        let buttonText: string

                        const statusName = solution.status.name
                        if (statusName === 'Approved' || statusName === 'Canceled') {
                            buttonText = labels.viewEvaluation
                            progressInfo = availableProgressInfo.finished;
                        } else {
                            buttonText = started ? labels.viewEvaluation : labels.evaluate
                            progressInfo = started ? availableProgressInfo.started : availableProgressInfo.pending
                        }
                        return (
                            <SolutionCard key={solution.id}>
                                <div style={{flex: 1}}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        marginBottom: '16px'
                                    }}>
                                        <h4 style={{fontSize: '16px', fontWeight: 600, color: '#212529', margin: 0}}>
                                            {solution.title}
                                        </h4>

                                    </div>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px'}}>

                                        <div
                                            style={{
                                                padding: '4px 12px',
                                                borderRadius: '4px',
                                                backgroundColor: progressInfo.bgColor,
                                                color: progressInfo.color,
                                                fontWeight: 500,
                                                display: 'inline-block'
                                            }}
                                        >
                                            {progressInfo.label}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <BaseButton
                                        variant={buttonText === labels.viewEvaluation ? 'secondary' : 'primary'}
                                        size="medium"
                                        btnContent={buttonText}
                                        onClick={() => navigate(`/hub/evaluator/challenge/${challengeId}/step/${stepId}/idea/${solution.id}`, {state: {stepName}})}
                                    />
                                </div>
                            </SolutionCard>
                        )
                    })}
                </div>
            </>
        )
    }

    return (
        <Container fluid className='p-0'>
            <MobileContainerPadding />
            <Row>
                <SolutionsWrapper>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px',
                        marginBottom: '24px'
                    }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                            <button
                                onClick={() => nativeNavigate(-1)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#212529',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <FontAwesomeIcon icon={['fas', 'arrow-left']} />
                            </button>
                            <ProgramTitle>{programName}</ProgramTitle>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Tippy
                                content={isExportDisabled ? labels.noDataToExport : labels.exportSpreadsheet}
                                followCursor={true}
                                plugins={[followCursor]}
                                animation="scale"
                                arrow={false}
                                theme="primary"
                            >
                                <span>
                                    <PdfButtonStyled>
                                        <BaseButton
                                            variant="secondary"
                                            size="medium"
                                            btnContent={labels.exportSpreadsheet}
                                            disabled={isExportDisabled}
                                            onClick={() => {
                                                if (isExportDisabled) return

                                                downloadFile(
                                                    {
                                                        url: `/v1/hub/evaluator/challenge/${challengeId}/report/first-step/detailed`,
                                                        httpConfig: {responseType: 'blob'}
                                                    },
                                                    {
                                                        onSuccess: (res) => {
                                                            triggerFileDownloadOnClientSide(res.data, 'hub_teia_compilado_etapa_inicial.xlsx')
                                                        },
                                                        onError: async (err) => {
                                                            const data = err?.response?.data
                                                            let message = 'Impossível exportar'

                                                            if (data instanceof Blob) {
                                                                const text = await data.text()
                                                                try {
                                                                    const parsed = JSON.parse(text)
                                                                    if (parsed?.Message) message = parsed.Message
                                                                } catch {
                                                                    if (text) message = text
                                                                }
                                                            }

                                                            showToast('error', message)
                                                        }
                                                    }
                                                )
                                            }}
                                        />
                                    </PdfButtonStyled>
                                </span>
                            </Tippy>

                            <BaseButton
                                variant="primary"
                                size="medium"
                                btnContent="Ver programa"
                                onClick={() =>
                                    navigate(`/hub/evaluator/challenge/${challengeId}`)
                                }
                            />
                        </div>
                    </div>

                    <StepInfoCard>
                        <FontAwesomeIcon icon={['fas', 'calendar-alt']} style={{fontSize: '20px', color: '#0d6efd'}}/>
                        <div>
                            <h3 style={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#212529',
                                margin: 0,
                                marginBottom: '4px'
                            }}>
                                {stepName}
                            </h3>
                            <p style={{ fontSize: '14px', color: '#6c757d', margin: 0 }}>
                                Período de avaliação: <br />{evaluationPeriod}
                            </p>
                        </div>
                    </StepInfoCard>

                    {((data?.rows?.length > 0) || currentSearchTerm !== '') && (
                        <Row className='mb-4' style={{ gap: '0' }}>
                            <Col xs={12} lg={6}>
                                <Filter currentSearchTerm={currentSearchTerm} btnClick={onSearch} />
                            </Col>
                        </Row>
                    )}
                </SolutionsWrapper>
            </Row>
            <Row>
                {renderContent()}
            </Row>
        </Container>
    )
}
